import { useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import {
  FeatureFlagsProvider,
  useFeatureFlags,
} from "./FeatureFlagsContext";
import { IndustryProvider } from "./IndustryContext";
import { getMyFeatureFlags } from "../api/platform";
import { DEFAULT_TENANT_INDUSTRY } from "../utils/industryLabels";

const REFRESH_MS = 20000;

/**
 * Overlays the signed-in company's module switches on the brand flags.
 * Platform admin has no company, so the brand snapshot stays as-is.
 */
export function SessionFeatureFlags({ children }) {
  const brandFlags = useFeatureFlags();
  const { user } = useAuth();
  const [tenantFlags, setTenantFlags] = useState(null);
  const [industry, setIndustry] = useState(
    () => user?.industry || DEFAULT_TENANT_INDUSTRY,
  );
  const isPlatformAdmin =
    user?.role === "platform-admin" ||
    user?.backendRoles?.includes("platform_admin");

  useEffect(() => {
    if (!user?.id || isPlatformAdmin) {
      setTenantFlags(null);
      setIndustry(DEFAULT_TENANT_INDUSTRY);
      return undefined;
    }

    // Login payload carries industry — apply immediately so labels don't
    // flash as "شركة" while /feature-flags is in flight or if it fails.
    if (user.industry) {
      setIndustry(user.industry);
    }

    let cancelled = false;

    async function load() {
      try {
        const data = await getMyFeatureFlags();
        if (cancelled) return;
        if (data?.flags && typeof data.flags === "object") {
          setTenantFlags(data.flags);
        }
        setIndustry(
          data?.industry || user.industry || DEFAULT_TENANT_INDUSTRY,
        );
      } catch {
        // Brand flags stay in place. Keep login industry if we have it.
        if (!cancelled && user.industry) {
          setIndustry(user.industry);
        }
      }
    }

    load();
    const timer = window.setInterval(load, REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("app:resume", load);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("app:resume", load);
    };
  }, [user?.id, user?.industry, isPlatformAdmin]);

  const flags = useMemo(
    () => (tenantFlags ? { ...brandFlags, ...tenantFlags } : brandFlags),
    [brandFlags, tenantFlags],
  );

  return (
    <IndustryProvider industry={industry}>
      <FeatureFlagsProvider flags={flags}>{children}</FeatureFlagsProvider>
    </IndustryProvider>
  );
}
